import { Grid, Link, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { point, bbox, circle } from "@turf/turf";
import isOnline from "is-online";
import { useCallback, useEffect, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppRoutes, { appRoutes } from "../AppRoutes";
import AppHeader from "../components/AppHeader";
import BodyContainer from "../components/BodyContainer";
import FooterButtons from "../components/FooterButtons";
import Map from "../components/Map";
import MapMarker from "../components/MapMarker";
import { crops, forestry, maxTonsFuelAllocations } from "../config/burns.constants";
import mapConfig from "../config/map.config";
import { getBurnsInsidePolygon, getCachedBurns } from "../services/burns.service";
import { getPlannedBurnsAreaPolygon } from "../services/map.service";
import { store } from "../store";
import { setErrorVisible } from "../store/cache.slice";
import { setRecommendation } from "../store/submission.slice";

const PlannedBurns = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const location = useSelector(state => state.submission.location);
	
	const [map, setMap] = useState();
	const [mapCenter, setMapCenter] = useState();

	const [burnSources, setBurnSources] = useState();
	const [plannedBurnPolygon, setPlannedBurnPolygon] = useState();

	const [totalTonnageForThisBurn, setTotalTonnageForThisBurn] = useState(0);
	const [totalTonnageForAllBurns, setTotalTonnageForAllBurns] = useState(null);
	const [allocatedTonnage, setAllocatedTonnage] = useState(0);
	const [totalAllowedAcres, setTotalAllowedAcres] = useState(null);

	useEffect(() => {
		if (!location) {
			dispatch(setErrorVisible('You must select a location.'))
			navigate(appRoutes.SelectLocation);
		} else {
			const center = point([location.coords.longitude, location.coords.latitude]);
			const visibleExtent = bbox(circle(center, 6, {units: 'miles'}));
			setMapCenter({ ...location.coords, zoom: 12.5, bounds: visibleExtent });

			setPlannedBurnPolygon(getPlannedBurnsAreaPolygon(center));
		}
	}, [location])

	useEffect(() => {
		if (!plannedBurnPolygon) return;

		const burns = getCachedBurns();
		if (burns) {
			burns.features = getBurnsInsidePolygon(burns.features, plannedBurnPolygon);
		}
		setBurnSources(burns ? [burns] : []);

		const burnTonnage = store.getState().submission.fuelLoad.totalTonnage;
		const totalAirshedTonnage = getTotalTonnage(burns ? burns.features : []);
		const maxTonsAlloc = getTotalAllocatedTonnage();
		const maxTonsRecomm = Math.max(maxTonsAlloc - totalAirshedTonnage, 0);
		const maxAcres = getMaxAcresRecommend(Math.max(0, maxTonsAlloc - totalAirshedTonnage));

		if (!burnTonnage || (maxTonsAlloc === undefined)) {
			dispatch(setErrorVisible('Could not retrieve the category day. Please try again.'))
			navigate(appRoutes.Home);
			return;
		}

		setTotalTonnageForThisBurn(burnTonnage);
		setAllocatedTonnage(maxTonsAlloc);

		if (burns && burns.features.length > 0) {
			setTotalTonnageForAllBurns(totalAirshedTonnage + burnTonnage);
		}

		if (maxTonsAlloc < (totalAirshedTonnage + burnTonnage)) {
			setTotalAllowedAcres(maxAcres);
		}

		dispatch(setRecommendation({
			totalTonnageForThisBurn: burnTonnage,
			totalTonnageForAllBurns: totalAirshedTonnage + burnTonnage,
			allocatedTonnage: maxTonsAlloc,
			totalAllowedTonnage: maxTonsRecomm,
			totalAllowedAcres: maxAcres
		}))
	}, [plannedBurnPolygon])

	const getTotalTonnage = useCallback((burns) => {
		const total = burns.reduce((prev,curr) => prev + (curr.properties.BurnInfo.FuelLoad.TotalTonnage || 0), 0);
		return total;
	}, [])

	const getTotalAllocatedTonnage = useCallback(() => {
		let ssaDist = store.getState().submission.distToClosestSSA;
		let catDay = store.getState().submission.weather.catDay;
		if (!catDay) return;
		if (!ssaDist || ssaDist === -1) ssaDist = Number.POSITIVE_INFINITY;
		catDay = parseInt(catDay);

		const allocInfo = maxTonsFuelAllocations.find(a => {
			return ssaDist >= a.distanceToSSA[0] && ssaDist <= a.distanceToSSA[1]
		});

		const maxAllocation = allocInfo ? allocInfo.tonsOfFuel[Math.max(0,catDay-1)] : null;
		return maxAllocation;
	}, [])

	const getMaxAcresRecommend = useCallback((maxTonsRecom) => {
		const {fuelLoadingRange, cropType} = store.getState().submission.fuelLoad;
		const crop = crops.find(c => c.name === cropType) || forestry.find(f => f.name === cropType);

		const tonsPerAcre = crop.fuelLoadingRange[fuelLoadingRange];
		return Math.floor(maxTonsRecom / (tonsPerAcre));
	}, [])

	const onMapLoaded = map => setMap(map);

	const onNext = () => {
		navigate(appRoutes.BurnInfo);
	}

	return (
		<>
			<AppHeader />

			<BodyContainer hasFooter={false} hasHeader={true} sx>
				<Grid item xs="auto" mb={4} mt={2} sx={{width: '100% !important'}}>
					<Typography variant="title" component="h1">Max Tons of Fuel Allocated to the Airshed</Typography>
					<Typography variant="subtitle2" component="h2">
						 The airshed allotment remaining for this day based on conditions and considering others who have reported prescribed fire 
						 activities within the airshed during the same time as you has been calculated. 
					</Typography>
				</Grid>
				<Grid item xs="auto" sx={{width: '100% !important'}}>
					{mapCenter &&
					<Map
					onMapLoad={onMapLoaded}
					mapLayers={[]}
					sources={burnSources}
					bounds={mapCenter.bounds}
					dragRotate={false}
					touchPitch={false}
					touchZoomRotate={false}
					doubleClickZoom={false}
					boxZoom={false}
					// scrollZoom={false}
					dragPan={false}>
						{plannedBurnPolygon &&
							<Source id="planned-burn-poly" type="geojson" data={plannedBurnPolygon}>
								<Layer id="burn-layer" source="burn-poly-src" type="fill" paint={{
									'fill-color': "rgba(169,169,169, .3)",
									'fill-opacity': 1,
									'fill-outline-color': 'gray'
								}} />
							</Source>
						}

						{location && <MapMarker longitude={location.coords.longitude} latitude={location.coords.latitude} />}
					</Map>
					}
				</Grid>
				{/* <Grid item xs="auto" mt={3} sx={{width: '100% !important'}}> */}
				<Grid item xs="auto" mt={3}>
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Total Tonnage of this Burn:
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{totalTonnageForThisBurn.toFixed(1)}
							</Box>
						</Box>
					</Typography>
					{totalTonnageForAllBurns !== null ?
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Total Tonnage within Airshed:
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{totalTonnageForAllBurns.toFixed(1)}
							</Box>
						</Box>
					</Typography>
					: null}
					{/* <Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Total Allowed Tonnage of this Burn:
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{totalAllowedTonnage.toFixed(1)}
							</Box>
						</Box>
					</Typography> */}
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Airshed Allotment Available (Tons):
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{allocatedTonnage.toFixed(1)}
							</Box>
						</Box>
					</Typography>
					{totalAllowedAcres !== null ? 
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Available Allotment Acreage:
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{totalAllowedAcres.toFixed(1)}
							</Box>
						</Box>
					</Typography>
					: null}
				</Grid>
				<Grid item xs="auto" mt={3} sx={{width: '100% !important'}}>
					<Typography variant="text" component="h2" mb={2}>
						 If the tons associated with your planned burn exceed the remaining allotment, it is recommended that you do not burn. 
					</Typography>
					<Typography variant="text" component="h2" mb={2}>
						 <i>You may go back to the “Determine Total Available Fuel Loading” screen to reduce the acreage for the planned burn.</i>
					</Typography>
					<Typography variant="text" component="h2" mb={2}>
						<b>Please contact the ADA Dispatch Center at <Link color="inherit" href="tel:1-800-830-8015">1-800-830-8015</Link> if you have questions. </b>
					</Typography>
				</Grid>
				<Grid item xs="auto" mt={4} sx={{width: '100% !important'}}>
					<FooterButtons backLabel="Go Back" onNext={onNext} nextDisabled={false} onBack={() => navigate(appRoutes.SmokeSensitiveAreas)} />
				</Grid>
			</BodyContainer>

			{/* <AppFooter /> */}
		</>
	)
}

export default PlannedBurns;